import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import groupService from '../api/groupService';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareAlt, faUserPlus, faChevronRight, faCopy, faTimes, faSearch, faFilter, faEllipsisV, faCheck, faXmark, faUsers, faUserClock, faUserCheck, faUpload } from '@fortawesome/free-solid-svg-icons';
import { copyToClipboardEnhanced } from '../utils/linkUtils';
import config from '../config';

// Import new modals
import AddMemberModal from './AddMemberModal';
import ImportMembersModal from './ImportMembersModal';
import ImportProcessingModal from './ImportProcessingModal';
import PasteInputModal from './PasteInputModal';
import FileUploadModal from './FileUploadModal';
import Toast from './Toast';
import { parseFile, parseText, applyAIProcessing, FileParseError, ERROR_TYPES } from '../utils/fileParser';
import stagingAPI from '../services/stagingAPI';

// Import styles
import '../styles/components/MemberActions.css';
import '../styles/FinancialsPage.css';

const MembersContent = ({ groupId }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stagingMembers, setStagingMembers] = useState([]);
  const [requestsError, setRequestsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasApiError, setHasApiError] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'members'
  const [sortBy, setSortBy] = useState('newest');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [memberToReactivate, setMemberToReactivate] = useState(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [requestToProcess, setRequestToProcess] = useState(null);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [denyLoading, setDenyLoading] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // New modals state
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showImportMembersModal, setShowImportMembersModal] = useState(false);
  const [showImportProcessingModal, setShowImportProcessingModal] = useState(false);
  const [showPasteInputModal, setShowPasteInputModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [importType, setImportType] = useState(null);
  const [importRawData, setImportRawData] = useState(null);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  
  // Toast notification state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success', details: null });

  // Helper function to show toast notifications
  const showToast = (message, type = 'success', details = null) => {
    setToast({ isVisible: true, message, type, details });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };
  
  // Persistent import state
  const [persistentImportState, setPersistentImportState] = useState(() => {
    try {
      const saved = localStorage.getItem(`yesod-import-state-${groupId || 'temp'}`);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading import state:', error);
      return null;
    }
  });

  // Clear import state when switching groups
  useEffect(() => {
    if (groupId) {
      // Clear modals when switching to a different group
      setShowAddMemberModal(false);
      setShowImportMembersModal(false);
      setShowImportProcessingModal(false);
      setShowPasteInputModal(false);
      setShowFileUploadModal(false);
      setImportType(null);
      setImportRawData(null);
      
      // Load new group's import state
      try {
        const saved = localStorage.getItem(`yesod-import-state-${groupId}`);
        setPersistentImportState(saved ? JSON.parse(saved) : null);
      } catch (error) {
        console.error('Error loading import state for new group:', error);
        setPersistentImportState(null);
      }
    }
  }, [groupId]);
  
  const modalRef = useRef(null);
  const shareUrlRef = useRef(null);

  // Logo state
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoError, setLogoError] = useState(false);

  // Logo handling
  useEffect(() => {
    if (group?.logo_url) {
      setLogoError(false);
      setLogoSrc(group.logo_url);
    }
  }, [group?.logo_url]);

  const handleLogoError = () => {
    console.log('Logo failed to load, using fallback');
    setLogoError(true);
    setLogoSrc(null);
  };

  // Save/restore persistent import state
  useEffect(() => {
    if (persistentImportState) {
      // Check if state is not too old (24 hours)
      const isStale = Date.now() - persistentImportState.timestamp > 24 * 60 * 60 * 1000;
      
      if (isStale) {
        // Clear stale state
        setPersistentImportState(null);
        try {
          localStorage.removeItem(`yesod-import-state-${groupId || 'temp'}`);
        } catch (error) {
          console.error('Error clearing stale import state:', error);
        }
        return;
      }
      
      // Restore import state on component mount
      if (persistentImportState.showImportProcessingModal) {
        setShowImportProcessingModal(true);
        setImportType(persistentImportState.importType);
        setImportRawData(persistentImportState.importRawData);
      } else if (persistentImportState.showPasteInputModal) {
        setShowPasteInputModal(true);
        setImportType(persistentImportState.importType);
      } else if (persistentImportState.showFileUploadModal) {
        setShowFileUploadModal(true);
        setImportType(persistentImportState.importType);
      }
    }
  }, []);

  // Save import state to localStorage whenever it changes
  useEffect(() => {
    const saveImportState = () => {
      const state = {
        showImportProcessingModal,
        showPasteInputModal,
        showFileUploadModal,
        importType,
        importRawData,
        timestamp: Date.now()
      };
      
      // Only save if there's an active import process
      if (showImportProcessingModal || showPasteInputModal || showFileUploadModal) {
        try {
          localStorage.setItem(`yesod-import-state-${groupId || 'temp'}`, JSON.stringify(state));
        } catch (error) {
          console.error('Error saving import state:', error);
        }
      } else {
        // Clear saved state when no import is active
        try {
          localStorage.removeItem(`yesod-import-state-${groupId || 'temp'}`);
        } catch (error) {
          console.error('Error clearing import state:', error);
        }
      }
    };

    saveImportState();
  }, [showImportProcessingModal, showPasteInputModal, showFileUploadModal, importType, importRawData, groupId]);

  // Manual save function for immediate persistence
  const savePersistentState = () => {
    const state = {
      showImportProcessingModal,
      showPasteInputModal,
      showFileUploadModal,
      importType,
      importRawData,
      timestamp: Date.now()
    };
    
    // Only save if there's an active import process
    if (showImportProcessingModal || showPasteInputModal || showFileUploadModal) {
      try {
        localStorage.setItem(`yesod-import-state-${groupId || 'temp'}`, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving import state:', error);
      }
    }
  };

  // Fetch group data and members
  useEffect(() => {
    const fetchData = async () => {
      if (dataFetched) return;
      
      // For new groups without a groupId, show empty state instead of loading
      if (!groupId) {
        setLoading(false);
        setDataFetched(true);
        setGroup({ name: 'Your Community' }); // Provide minimal group data for empty state
        setMembers([]);
        setRequests([]);
        return;
      }
      
      setLoading(true);
      setError('');
      
      try {
        // Fetch group details
        const groupRes = await groupService.getGroup(groupId);
        setGroup(groupRes.data);

        // Fetch members with details
        const membersRes = await groupService.getGroupMembersWithDetails(groupId);
        setMembers(membersRes.data || []);

        // Fetch pending requests
        const requestsRes = await groupService.getPendingRequests(groupId);
        setRequests(requestsRes || []);

        // Fetch staging members (invited members from imports)
        try {
          const stagingRes = await stagingAPI.getStagingMembers(groupId, 'pending');
          setStagingMembers(stagingRes || []);
        } catch (stagingError) {
          console.warn('Could not fetch staging members:', stagingError);
          setStagingMembers([]);
        }

        setDataFetched(true);
      } catch (err) {
        console.error('Error fetching members data:', err);
        setError('Please refresh the page to reload data');
        setHasApiError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId, dataFetched]);

  // Force refresh data - useful for debugging or manual refresh
  const refreshData = async () => {
    if (!groupId) {
      // For new groups without groupId, just maintain empty state
      setLoading(false);
      setGroup({ name: 'Your Community' });
      setMembers([]);
      setRequests([]);
      return;
    }
    
    setDataFetched(false);
    setLoading(true);
    
    try {
      // Fetch fresh data
      const [groupRes, membersRes, requestsRes] = await Promise.all([
        groupService.getGroup(groupId),
        groupService.getGroupMembersWithDetails(groupId),
        groupService.getPendingRequests(groupId)
      ]);

      setGroup(groupRes.data);
      setMembers(membersRes.data || []);
      setRequests(requestsRes || []);
      
      // Fetch staging members separately (non-critical if it fails)
      try {
        const stagingRes = await stagingAPI.getStagingMembers(groupId, 'pending');
        setStagingMembers(stagingRes || []);
      } catch (stagingError) {
        console.warn('Could not fetch staging members:', stagingError);
        setStagingMembers([]);
      }
      
      console.log('🔄 Data refreshed:', {
        members: membersRes.data?.length || 0,
        requests: requestsRes?.length || 0,
        staging: stagingMembers?.length || 0
      });
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Please refresh the page to reload data');
    } finally {
      setLoading(false);
      setDataFetched(true);
    }
  };

  // Add refresh data on window focus (when user comes back to the tab)
  // Only refresh if no modals are open and sufficient time has passed
  useEffect(() => {
    let lastFocusTime = 0;
    const FOCUS_DEBOUNCE_TIME = 5000; // 5 seconds minimum between refreshes
    
    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastFocus = now - lastFocusTime;
      
      // Check if any import modals are open
      const hasActiveModal = showAddMemberModal || showImportMembersModal || 
                           showImportProcessingModal || showPasteInputModal || 
                           showFileUploadModal;
      
      // Only refresh if:
      // 1. No modals are open
      // 2. Enough time has passed since last refresh
      // 3. User actually switched away from the tab (not just mouse movement)
      if (!hasActiveModal && timeSinceLastFocus > FOCUS_DEBOUNCE_TIME) {
        console.log('🔄 Window focused, refreshing data...');
        lastFocusTime = now;
        refreshData();
      } else {
        console.log('🔄 Skipping refresh - modal active or too recent');
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [groupId, showAddMemberModal, showImportMembersModal, showImportProcessingModal, showPasteInputModal, showFileUploadModal]);

  // Handle approve request
  const handleApprove = async (requestId) => {
    const request = requests.find(r => r.id === requestId);
    setRequestToProcess(request);
    setShowAcceptModal(true);
  };

  // Handle deny request
  const handleDeny = async (requestId) => {
    const request = requests.find(r => r.id === requestId);
    setRequestToProcess(request);
    setShowDenyModal(true);
  };

  // Confirm accept request
  const confirmAcceptRequest = async () => {
    if (!requestToProcess) return;
    
    setAcceptLoading(true);
    try {
      await groupService.approveJoinRequest(groupId, requestToProcess.id);
      setRequests(requests.filter(req => req.id !== requestToProcess.id));
      // Refresh members list
      const membersRes = await groupService.getGroupMembersWithDetails(groupId);
      setMembers(membersRes.data || []);
      setShowAcceptModal(false);
      setRequestToProcess(null);
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request');
    } finally {
      setAcceptLoading(false);
    }
  };

  // Confirm deny request
  const confirmDenyRequest = async () => {
    if (!requestToProcess) return;
    
    setDenyLoading(true);
    try {
      await groupService.denyJoinRequest(groupId, requestToProcess.id);
      setRequests(requests.filter(req => req.id !== requestToProcess.id));
      setShowDenyModal(false);
      setRequestToProcess(null);
    } catch (error) {
      console.error('Error denying request:', error);
      alert('Failed to deny request');
    } finally {
      setDenyLoading(false);
    }
  };

  // Cancel request actions
  const cancelRequestAction = () => {
    setShowAcceptModal(false);
    setShowDenyModal(false);
    setRequestToProcess(null);
  };

  // Handle bulk approve
  const handleBulkApprove = async () => {
    setShowBulkApproveModal(true);
  };

  // Confirm bulk approve
  const confirmBulkApprove = async () => {
    setBulkLoading(true);
    try {
      for (const request of requests) {
        await groupService.approveJoinRequest(groupId, request.id);
      }
      setRequests([]);
      // Refresh members list
      const membersRes = await groupService.getGroupMembersWithDetails(groupId);
      setMembers(membersRes.data || []);
      setShowBulkApproveModal(false);
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to approve all requests');
    } finally {
      setBulkLoading(false);
    }
  };

  // Cancel bulk approve
  const cancelBulkApprove = () => {
    setShowBulkApproveModal(false);
  };

  // Handle dropdown toggle
  const toggleDropdown = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside AND not on a dropdown trigger
      if (!event.target.closest('.dropdown')) {
        setOpenDropdown(null);
      }
    };

    // Add a small delay to prevent immediate closing
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdown]);

  // Handle remove member (regular members or staging members)
  const handleRemoveMember = async (userId) => {
    // Check if this is a staging member (invited)
    if (userId && userId.toString().startsWith('staging_')) {
      const stagingId = userId.replace('staging_', '');
      const stagingMember = stagingMembers.find(s => s.id === stagingId);
      setMemberToRemove({
        ...stagingMember,
        user_id: userId,
        isStaging: true,
        staging_id: stagingId
      });
    } else {
      // Regular member
      const member = members.find(m => m.user_id === userId);
      setMemberToRemove({
        ...member,
        isStaging: false
      });
    }
    setShowRemoveModal(true);
  };

  // Confirm remove member
  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;
    
    setRemoveLoading(true);
    try {
      if (memberToRemove.isStaging) {
        // Remove from staging table
        await stagingAPI.removeStagingRecords([memberToRemove.staging_id], groupId);
        
        // Remove from staging members state (optimistic update)
        setStagingMembers(stagingMembers.filter(s => s.id !== memberToRemove.staging_id));
        
        console.log(`✅ Removed staging member: ${memberToRemove.name || memberToRemove.email}`);
        
        // Add small delay then refresh data to ensure consistency
        setTimeout(async () => {
          try {
            await refreshData();
          } catch (refreshError) {
            console.warn('Error refreshing data after staging removal:', refreshError);
          }
        }, 500);
      } else {
        // Regular member - mark as blocked
        await groupService.removeMember(groupId, memberToRemove.user_id);
        
        // Update member status to 'blocked' instead of removing them from UI
        setMembers(members.map(member => 
          member.user_id === memberToRemove.user_id 
            ? { ...member, status: 'blocked' }
            : member
        ));
        
        console.log(`✅ Blocked regular member: ${memberToRemove.user?.name || memberToRemove.user?.email}`);
      }
      
      setShowRemoveModal(false);
      setMemberToRemove(null);
    } catch (error) {
      console.error('Error removing member:', error);
      const memberType = memberToRemove.isStaging ? 'invited member' : 'member';
      alert(`Failed to remove ${memberType}. Please try again.`);
    } finally {
      setRemoveLoading(false);
    }
  };

  // Handle reactivate member
  const handleReactivateMember = async (userId) => {
    const member = members.find(m => m.user_id === userId);
    if (!member) return;
    
    setMemberToReactivate(member);
    setShowReactivateModal(true);
  };

  // Confirm reactivate member
  const confirmReactivateMember = async () => {
    if (!memberToReactivate) return;
    
    setReactivateLoading(true);
    try {
      await groupService.activateMember(groupId, memberToReactivate.user_id);
      
      // Update member status to 'active'
      setMembers(members.map(member => 
        member.user_id === memberToReactivate.user_id 
          ? { ...member, status: 'active' }
          : member
      ));
      
      setShowReactivateModal(false);
      setMemberToReactivate(null);
    } catch (error) {
      console.error('Error reactivating member:', error);
      alert('Failed to reactivate member. Please try again.');
    } finally {
      setReactivateLoading(false);
    }
  };

  // Cancel reactivate member
  const cancelReactivateMember = () => {
    setShowReactivateModal(false);
    setMemberToReactivate(null);
  };

  // Cancel remove member
  const cancelRemoveMember = () => {
    setShowRemoveModal(false);
    setMemberToRemove(null);
  };

  // Handle invite member (add to staging table)
  const handleAddMember = async (memberData) => {
    setAddMemberLoading(true);
    try {
      console.log('Adding member to staging table:', memberData);
      
      // Convert memberData to the format expected by staging API
      const fullName = memberData.name || `${memberData.firstName || ''} ${memberData.lastName || ''}`.trim();
      const stagingMember = {
        name: fullName,
        firstName: memberData.firstName || memberData.name?.split(' ')[0] || '',
        lastName: memberData.lastName || memberData.name?.split(' ').slice(1).join(' ') || '',
        email: memberData.email,
        phone: memberData.phone || null
      };
      
      // Add member to staging table using the same API as import
      console.log('🔍 About to call stagingAPI.stageMembersImport with:', {
        groupId,
        stagingMember,
        importSource: 'manual',
        importType: 'manual_add'
      });
      
      await stagingAPI.stageMembersImport(
        groupId,
        [stagingMember], // Array with single member
        'manual', // import_source
        'manual_add' // import_type
      );
      
      console.log(`✅ Successfully added ${fullName} to staging table`);
      
      // Show success toast
          showToast(
            `Added ${fullName} to your community`,
            'success',
            'They will activate via the app'
          );
          
      // Add small delay then refresh data to ensure database consistency
      setTimeout(async () => {
        try {
          await refreshData();
        } catch (refreshError) {
          console.warn('Error refreshing data after member addition:', refreshError);
        }
      }, 500);
      
      // Close the modal
      setShowAddMemberModal(false);
      
    } catch (error) {
      console.error('Error adding member to staging:', error);
      throw error; // Let modal handle error display
    } finally {
      setAddMemberLoading(false);
    }
  };

  // Handle import option selection
  const handleImportOption = (option, data = null) => {
    setImportType(option);
    setShowImportMembersModal(false);
    
    if (option === 'paste') {
      setShowPasteInputModal(true);
    } else if (option === 'upload') {
      if (data) {
        // If file data is provided directly (drag-drop), go straight to processing
        setImportRawData(data);
        setShowImportProcessingModal(true);
      } else {
        // Show file upload modal for file selection
        setShowFileUploadModal(true);
      }
    }
  };

  // Handle paste input next
  const handlePasteNext = async (textData) => {
    try {
      console.log('🔍 Pasted content:', textData);
      console.log('🔍 Content length:', textData.length);
      console.log('🔍 Lines found:', textData.split('\n').length);
      
      // Import the file parser
      const { parseText } = await import('../utils/fileParser');
      
      // Parse the pasted content
      const parsedData = parseText(textData);
      console.log('🔍 Parsed data:', parsedData);
      console.log('🔍 Parsed data length:', parsedData?.length);
      
      // Log each parsed member
      if (parsedData && parsedData.length > 0) {
        parsedData.forEach((member, index) => {
          console.log(`🔍 Member ${index}:`, {
            firstName: member.firstName,
            lastName: member.lastName, 
            email: member.email,
            phone: member.phone,
            rawText: member.rawText
          });
        });
        
        setImportRawData(parsedData);
        setImportType('paste');
        setShowPasteInputModal(false);
        setShowImportProcessingModal(true);
        savePersistentState();
      } else {
        console.log('⚠️ No valid contact data found');
        alert('No valid contact data found in the pasted content. Please check the format and try again.');
      }
    } catch (error) {
      console.error('Error processing pasted content:', error);
      alert('Error processing pasted content. Please check the format and try again.');
    }
  };

  // Handle file upload next
  const handleFileUploadNext = async (file) => {
    try {
      // Parse the uploaded file
      const parsedData = await parseFile(file);
      
      if (parsedData && parsedData.length > 0) {
        setImportRawData(parsedData);
        setImportType('upload');
        setShowFileUploadModal(false);
        setShowImportProcessingModal(true);
        savePersistentState();
      } else {
        // This shouldn't happen with the new parser, but just in case
        setFileUploadError({
          type: ERROR_TYPES.NO_CONTACT_INFO,
          message: 'No valid contact data found in the file',
          suggestions: [
            'Make sure your file contains names, emails, or phone numbers',
            'Check that your data is properly formatted',
            'Try using the copy & paste option instead'
          ]
        });
      }
    } catch (error) {
      console.error('Error processing file:', error);
      
      if (error instanceof FileParseError) {
        // Show detailed error information
        setFileUploadError({
          type: error.type,
          message: error.message,
          suggestions: error.suggestions || []
        });
      } else {
        // Fallback for unexpected errors
        setFileUploadError({
          type: ERROR_TYPES.CORRUPTED_FILE,
          message: 'Unexpected error processing file',
          suggestions: [
            'Try re-downloading or re-exporting your file',
            'Make sure the file is not corrupted',
            'Contact support if the problem persists'
          ]
        });
      }
    }
  };

  // Handle import back buttons
  const handleImportBack = () => {
    if (showImportProcessingModal) {
      if (importType === 'paste') {
        setShowImportProcessingModal(false);
        setShowPasteInputModal(true);
      } else if (importType === 'upload') {
        setShowImportProcessingModal(false);
        setShowFileUploadModal(true);
      } else {
        setShowImportProcessingModal(false);
        setShowImportMembersModal(true);
      }
    } else if (showPasteInputModal) {
      setShowPasteInputModal(false);
      setShowImportMembersModal(true);
    } else if (showFileUploadModal) {
      setShowFileUploadModal(false);
      setShowImportMembersModal(true);
    }
  };

  // Handle confirm import
  const handleConfirmImport = async (membersToImport) => {
    try {
      console.log('🚀 Starting import process from MembersContent...');
      console.log('📊 Import data:', {
        groupId,
        membersCount: membersToImport.length,
        importType,
        sampleMember: membersToImport[0]
      });
      
      // Stage members for native app integration
      if (groupId && membersToImport.length > 0) {
        console.log('📋 Staging members for native app integration...');
        
        // Import staging API
        const { default: stagingAPI } = await import('../services/stagingAPI');
        
        const stagingResult = await stagingAPI.stageMembersImport(
          groupId,
          membersToImport,
          importType === 'paste' ? 'paste' : 'upload',
          importType || 'manual'
        );
        
        console.log('📤 Staging API response:', stagingResult);
        console.log(`✅ Staged ${stagingResult.staged_count} members`);
        
        // Show success message
        if (stagingResult.staged_count > 0) {
          const importTypeText = importType === 'paste' ? 'pasted' : 'uploaded';
          showToast(
            `Added ${stagingResult.staged_count} member${stagingResult.staged_count === 1 ? '' : 's'} to your community`,
            'success',
            'They will activate via the app'
          );
        } else {
          showToast(
            'No members were staged',
            'warning', 
            'Please check that your import data contains valid member information.'
          );
        }
      } else {
        console.log('⚠️ Skipping staging - missing groupId or no members');
        showToast(
          'Import failed - missing required data',
          'error',
          'Please try again or contact support if the problem persists.'
        );
        return;
      }
      
      // Clear import state after successful import
      handleCloseAllModals();
      
      // Add small delay then refresh data to ensure database consistency
      setTimeout(async () => {
        try {
          await refreshData();
        } catch (refreshError) {
          console.warn('Error refreshing data after bulk import:', refreshError);
        }
      }, 500);
      
    } catch (error) {
      console.error('❌ Error importing members:', error);
      showToast(
        'Import failed',
        'error',
        error.message || 'An unexpected error occurred. Please try again.'
      );
      throw error;
    }
  };

  // Handle modal closes
  const handleCloseAllModals = () => {
    setShowAddMemberModal(false);
    setShowImportMembersModal(false);
    setShowImportProcessingModal(false);
    setShowPasteInputModal(false);
    setShowFileUploadModal(false);
    setImportType(null);
    setImportRawData(null);
    setFileUploadError(null);
    hideToast(); // Clear any visible toasts
    
    // Clear persistent state when user intentionally closes
    setPersistentImportState(null);
    try {
      localStorage.removeItem(`yesod-import-state-${groupId || 'temp'}`);
    } catch (error) {
      console.error('Error clearing import state:', error);
    }
  };

  // Handle share
  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCloseModal = () => {
    setShowShareModal(false);
    setCopySuccess(false);
  };

  const handleCopyUrl = async () => {
    try {
      const shareUrl = `${window.location.origin}/group/${groupId}`;
      await copyToClipboardEnhanced(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  // Filter and search functionality
  const filteredData = () => {
    let allData = [];
    
    // Add requests with type 'pending'
    const requestsWithType = requests.map(request => ({
      ...request,
      type: 'pending',
      status: 'Pending',
      name: request.name || request.user?.name || 'Unknown',
      email: request.email || request.user?.email || '',
      phone: request.phone || request.user?.phone || '',
      date: request.requested_at || new Date().toISOString()
    }));
    
    // Add members with type 'member'
    const membersWithType = members.map(member => ({
      ...member,
      type: 'member',
      status: member.status || 'active', // Use actual member status (active/blocked)
      name: member.user?.name || 'Unknown',
      email: member.user?.email || '',
      phone: member.user?.phone || '',
      date: member.joined_at || new Date().toISOString()
    }));

    // Add staging members with type 'invited'
    const stagingWithType = stagingMembers.map(staging => ({
      ...staging,
      type: 'invited',
      status: 'Invited',
      name: staging.name || (staging.first_name && staging.last_name ? 
        `${staging.first_name} ${staging.last_name}` : 
        staging.first_name || staging.last_name || 'Unknown'),
      email: staging.email || '',
      phone: staging.phone || '',
      date: null, // Invited members should show blank in Joined at column
      user_id: `staging_${staging.id}`, // Unique ID to avoid conflicts
      import_source: staging.import_source,
      expires_at: staging.expires_at
    }));
    
    // Combine based on active tab
    if (activeTab === 'pending') {
      allData = requestsWithType;
    } else if (activeTab === 'members') {
      allData = membersWithType;
    } else {
      allData = [...requestsWithType, ...membersWithType, ...stagingWithType];
    }
    
    // Apply search filter
    if (searchQuery) {
      allData = allData.filter(item => 
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply sorting
    allData.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'oldest':
          return new Date(a.date) - new Date(b.date);
        case 'newest':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });
    
    return allData;
  };

  if (loading) {
    return (
      <div className="financials-page">
        <div className="financials-container">
          <div className="page-header">
            <div className="page-header-top">
              <h1>Members</h1>
            </div>
          </div>
          <div className="loading-state">
            <p>Loading member data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && hasApiError) {
    return (
      <div className="financials-page">
        <div className="financials-container">
          <div className="page-header">
            <div className="page-header-top">
              <h1>Members</h1>
            </div>
          </div>
          <div className="error-state" style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px',
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  const renderEmptyState = () => {
    // If there are no members AND no requests at all
    if (members.length === 0 && requests.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Members Yet</h3>
          <p className="text-gray-600 text-center max-w-md">
            No one has joined your space yet. Share the Yesod app with your community so they can request to join.
          </p>
        </div>
      );
    }
    
    // If there is data but filtered results are empty (due to search/tabs)
    if (filteredData().length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
          <p className="text-gray-600 text-center max-w-md">
            Try adjusting your search or filter settings
          </p>
        </div>
      );
    }
    
    return null;
  };

  // Prepare filter components
  const renderFilters = () => (
    <div className="chip-filters-container">
      <div className="chip-filters">
        <button
          className={`chip-filter ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Users
        </button>
        <button
          className={`chip-filter ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Active Members
        </button>
        <button
          className={`chip-filter ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({requests.length})
        </button>
        
        {requests.length > 0 && activeTab === 'pending' && (
          <button 
            className="chip-filter add-chip"
            onClick={handleBulkApprove}
            disabled={bulkLoading}
            title={`Accept All (${requests.length})`}
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        )}
      </div>
      
      {/* Right side controls - Search Bar */}
      <div className="right-controls">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
          </div>
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-72 pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
      </div>
    </div>
  );

  const renderSummaryBoxes = () => (
    <div className="summary-boxes">
      <div 
        className="summary-box clickable-stat" 
        onClick={() => {
          console.log('📊 Clicked Total Users - switching to all');
          setActiveTab('all');
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="summary-content">
          <div className="summary-left">
            <div className="summary-label">
              <FontAwesomeIcon icon={faUsers} className="status-icon" style={{ color: '#3b82f6' }} />
              Total Members
            </div>
            <div className="summary-value">{members.length + requests.length}</div>
          </div>
        </div>
      </div>
      
      <div 
        className="summary-box clickable-stat" 
        onClick={() => {
          console.log('📊 Clicked Pending Requests - switching to pending');
          setActiveTab('pending');
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className="summary-content">
          <div className="summary-left">
            <div className="summary-label">
              <FontAwesomeIcon icon={faUserClock} className="status-icon" style={{ color: '#f59e0b' }} />
              Pending Requests
            </div>
            <div className="summary-value">{requests.length}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="financials-page">
      <div className="financials-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-top">
            <h1>Members</h1>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setShowImportMembersModal(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Import
              </button>
              <button 
                onClick={() => setShowAddMemberModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                Add Member
              </button>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="error-state" style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: '8px', 
            padding: '16px', 
            marginBottom: '24px',
            color: '#dc2626'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Loading State */}
        {loading && members.length === 0 && requests.length === 0 ? (
          <div className="loading-state">
            <p>Loading members...</p>
          </div>
        ) : (
          <>
            {/* Summary Boxes */}
            {renderSummaryBoxes()}

            {/* Filters */}
            {renderFilters()}

            {/* Members Table or Empty State */}
            {filteredData().length > 0 ? (
              <div className="table-wrapper">
            <table className="donation-table">
              <thead>
                <tr>
                  <th className="col-donor">Name</th>
                  <th className="col-type">Email</th>
                  <th className="col-date">Phone</th>
                  <th className="col-date">Joined App</th>
                  <th className="col-status">App Status</th>
                  <th className="col-amount">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData().map((item, index) => (
                    <tr 
                      key={`${item.type}-${item.id}-${index}`} 
                      className={`donation-row ${
                        item.type === 'pending' ? 'pending' : 
                        item.type === 'invited' ? 'pending' :
                        item.status === 'blocked' ? 'failed' : 'succeeded'
                      } ${item.type !== 'pending' && item.type !== 'invited' ? 'clickable-row' : ''}`}
                      onClick={() => {
                        if (item.type !== 'pending' && item.type !== 'invited' && item.user_id) {
                          console.log('🟢 Navigating to member profile:', item.user_id);
                          navigate(`/admin/dashboard/${groupId}/members/${item.user_id}`, {
                            state: { memberData: item }
                          });
                        }
                      }}
                      style={{
                        cursor: (item.type !== 'pending' && item.type !== 'invited') ? 'pointer' : 'default'
                      }}
                    >
                      <td className="col-donor">
                        <div className="donor-info">
                          <div className="donor-name">{item.name}</div>
                        </div>
                      </td>
                      <td className="col-type">
                        <div className="donor-email text-muted">
                          {item.email || 'No email'}
                        </div>
                      </td>
                      <td className="col-date">
                        <div className="date-cell">
                          <div className="date-primary">{item.phone || '—'}</div>
                        </div>
                      </td>
                      <td className="col-date">
                        <div className="date-cell">
                          <div className="date-primary">
                            {item.date ? new Date(item.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : '—'}
                          </div>
                        </div>
                      </td>
                      <td className="col-status">
                        <div 
                          className="status-badge" 
                          style={{ 
                            color: item.type === 'pending' ? '#f59e0b' : 
                                   item.type === 'invited' ? '#3b82f6' :
                                   item.status === 'blocked' ? '#ef4444' : '#10b981',
                            backgroundColor: item.type === 'pending' ? '#fffbeb' : 
                                            item.type === 'invited' ? '#eff6ff' :
                                            item.status === 'blocked' ? '#fef2f2' : '#ecfdf5'
                          }}
                        >
                          <span>
                            {item.type === 'pending' ? 'Pending' : 
                             item.type === 'invited' ? 'Invited' :
                             item.status === 'blocked' ? 'Blocked' : 'Active'}
                          </span>
                        </div>
                      </td>
                      <td className="col-amount">
                        <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
                          {item.type === 'pending' ? (
                            <>
                              <button 
                                className="approve-button-compact"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprove(item.id);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  borderRadius: '6px',
                                  border: '1px solid #10b981',
                                  background: '#ecfdf5',
                                  color: '#10b981',
                                  cursor: 'pointer'
                                }}
                              >
                                Accept
                              </button>
                              <button 
                                className="deny-button-compact"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeny(item.id);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  borderRadius: '6px',
                                  border: '1px solid #ef4444',
                                  background: '#fef2f2',
                                  color: '#ef4444',
                                  cursor: 'pointer'
                                }}
                              >
                                Remove
                              </button>
                            </>
                          ) : item.status === 'blocked' ? (
                            <button 
                              className="approve-button-compact reactivate-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReactivateMember(item.user_id);
                              }}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                borderRadius: '6px',
                                border: '1px solid #10b981',
                                background: '#ecfdf5',
                                color: '#10b981',
                                cursor: 'pointer'
                              }}
                            >
                              Reactivate
                            </button>
                          ) : (
                            <button 
                              className="deny-button-compact remove-button-red"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveMember(item.user_id);
                              }}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                borderRadius: '6px',
                                border: '1px solid #ef4444',
                                background: '#fef2f2',
                                color: '#ef4444',
                                cursor: 'pointer'
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            
            <div style={{ padding: '20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', textAlign: 'center' }}>
              <div style={{ color: '#64748b', fontSize: '14px' }}>
                Showing {filteredData().length} of {members.length + requests.length + stagingMembers.length} members
              </div>
            </div>
          </div>
            ) : (
              renderEmptyState()
            )}
          </>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modern-modal" onClick={e => e.stopPropagation()} ref={modalRef}>
            <div className="modal-header">
              <h3>Community Page</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Share this link with others to invite them to join your group:</p>
              <div className="url-container">
                <input 
                  type="text" 
                  value={`${window.location.origin}/group/${groupId}`}
                  readOnly 
                  ref={shareUrlRef}
                  className="url-input"
                />
                <button 
                  className={`copy-btn ${copySuccess ? 'success' : ''}`}
                  onClick={handleCopyUrl}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  {copySuccess ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveModal && memberToRemove && (
        <div className="modal-overlay" onClick={cancelRemoveMember}>
          <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{memberToRemove.isStaging ? 'Remove Invitation' : 'Remove Member'}</h3>
              <button className="close-button" onClick={cancelRemoveMember}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              {memberToRemove.isStaging ? (
                <>
                  <p>Are you sure you want to remove the invitation for <strong>{memberToRemove.name || memberToRemove.email || 'this person'}</strong>?</p>
                  <p className="warning-text">This will permanently delete their invitation. They will not be automatically added to your community if they join the app.</p>
                </>
              ) : (
                <>
                  <p>Are you sure you want to remove <strong>{memberToRemove.user?.name || 'this member'}</strong> from the community?</p>
                  <p className="warning-text">This will remove their access to your community on the Yesod app and cannot be undone.</p>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={cancelRemoveMember}
                disabled={removeLoading}
              >
                Cancel
              </button>
              <button 
                className="remove-confirm-button" 
                onClick={confirmRemoveMember}
                disabled={removeLoading}
              >
                {removeLoading ? 'Removing...' : (memberToRemove.isStaging ? 'Remove Invitation' : 'Remove Member')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Member Confirmation Modal */}
      {showReactivateModal && memberToReactivate && (
        <div className="modal-overlay" onClick={cancelReactivateMember}>
          <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reactivate Member</h3>
              <button className="close-button" onClick={cancelReactivateMember}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reactivate <strong>{memberToReactivate.user?.name || 'this member'}</strong>?</p>
              <p className="info-text">This will restore their access to your community on the Yesod app and they will be able to see events, classes, and prayer times again.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={cancelReactivateMember}
                disabled={reactivateLoading}
              >
                Cancel
              </button>
              <button 
                className="accept-confirm-button" 
                onClick={confirmReactivateMember}
                disabled={reactivateLoading}
              >
                {reactivateLoading ? 'Reactivating...' : 'Reactivate Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Request Confirmation Modal */}
      {showAcceptModal && requestToProcess && (
        <div className="modal-overlay" onClick={cancelRequestAction}>
          <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Accept Join Request</h3>
              <button className="close-button" onClick={cancelRequestAction}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>Accept <strong>{requestToProcess.user?.name || 'this user'}</strong> into the community?</p>
              <p className="info-text">They will gain access to community events, classes, and prayer times in the Yesod app.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={cancelRequestAction}
                disabled={acceptLoading}
              >
                Cancel
              </button>
              <button 
                className="accept-confirm-button" 
                onClick={confirmAcceptRequest}
                disabled={acceptLoading}
              >
                {acceptLoading ? 'Processing...' : 'Accept Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deny Request Confirmation Modal */}
      {showDenyModal && requestToProcess && (
        <div className="modal-overlay" onClick={cancelRequestAction}>
          <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Deny Join Request</h3>
              <button className="close-button" onClick={cancelRequestAction}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>Deny <strong>{requestToProcess.user?.name || 'this user'}</strong>'s request to join?</p>
              <p className="warning-text">This will prevent them from accessing the community in the Yesod app.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={cancelRequestAction}
                disabled={denyLoading}
              >
                Cancel
              </button>
              <button 
                className="deny-confirm-button" 
                onClick={confirmDenyRequest}
                disabled={denyLoading}
              >
                {denyLoading ? 'Processing...' : 'Deny Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Accept All Confirmation Modal */}
      {showBulkApproveModal && (
        <div className="modal-overlay" onClick={cancelBulkApprove}>
          <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Accept All Requests</h3>
              <button className="close-button" onClick={cancelBulkApprove}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>Accept all <strong>{requests.length} pending requests</strong>?</p>
              <p className="info-text">All users will gain access to community events, classes, and prayer times in the Yesod app.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={cancelBulkApprove}
                disabled={bulkLoading}
              >
                Cancel
              </button>
              <button 
                className="accept-confirm-button" 
                onClick={confirmBulkApprove}
                disabled={bulkLoading}
              >
                {bulkLoading ? 'Processing...' : `Accept All (${requests.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Member Management Modals */}
      <AddMemberModal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        onMemberAdded={handleAddMember}
        loading={addMemberLoading}
      />

      <ImportMembersModal
        isOpen={showImportMembersModal}
        onClose={handleCloseAllModals}
        onImportOption={handleImportOption}
      />

      <PasteInputModal
        isOpen={showPasteInputModal}
        onClose={handleCloseAllModals}
        onBack={handleImportBack}
        onNext={handlePasteNext}
      />

      <FileUploadModal
        isOpen={showFileUploadModal}
        onClose={handleCloseAllModals}
        onNext={handleFileUploadNext}
        onBack={handleImportBack}
        externalError={fileUploadError}
        onClearError={() => setFileUploadError(null)}
      />

      <ImportProcessingModal
        isOpen={showImportProcessingModal}
        onClose={handleCloseAllModals}
        onBack={handleImportBack}
        importType={importType}
        rawData={importRawData}
        onConfirmImport={handleConfirmImport}
        groupId={groupId}
      />

      {/* Toast notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        details={toast.details}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={5000}
      />
    </div>
  );
};

export default MembersContent;
